import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import { LightningIcon, PulseIcon, MoonIcon, WarningIcon, PauseIcon } from "@phosphor-icons/react";

const STATUS_META = {
  running:  { label: "RUNNING",  color: "#D6FF38", Icon: LightningIcon, pulse: true  },
  sleeping: { label: "SLEEPING", color: "#60A5FA", Icon: MoonIcon,      pulse: false },
  stopped:  { label: "STOPPED",  color: "#94A3B8", Icon: PauseIcon,     pulse: false },
  stale:    { label: "STALE",    color: "#FFB400", Icon: WarningIcon,   pulse: false },
  offline:  { label: "OFFLINE",  color: "#64748B", Icon: WarningIcon,   pulse: false },
};

function fmtElapsed(startedAt) {
  if (!startedAt) return "—";
  const ms = Date.now() - new Date(startedAt).getTime();
  if (isNaN(ms) || ms < 0) return "—";
  // Clamp: if elapsed is more than 12 hours, the upstream timestamp is
  // almost certainly stale (VPS clock skew, replayed seed, dead loop).
  // Show — rather than a nonsensical 3900h value.
  if (ms > 12 * 3600 * 1000) return "stale";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

const BAR_COLOR = (r) => {
  if (r === "OK") return "#2ECA7F";
  if (r && r.startsWith("EXIT_")) return "#FFB400";
  if (r === "EXCEPTION") return "#FF4D4D";
  return "#94A3B8";
};

const CHART_MARGIN = { top: 4, right: 4, left: 0, bottom: 0 };
const CURSOR_STYLE = { fill: "#2A2D3A", opacity: 0.3 };
const TOOLTIP_STYLE = { background: "#15171E", border: "1px solid #2A2D3A", borderRadius: 6, fontSize: 12 };
const X_TICK_STYLE = { fontSize: 10, fill: "#64748B", fontFamily: "ui-monospace,monospace" };

export default function AutopilotTile() {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchOnce = () => {
      api.get("/loop/status")
        .then((r) => { if (alive) setData(r.data); })
        .catch(() => { if (alive) setData({ state: { status: "offline" }, history: [] }); });
    };
    fetchOnce();
    const poll = setInterval(fetchOnce, 10000);
    const elapse = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { alive = false; clearInterval(poll); clearInterval(elapse); };
  }, []);

  if (!data) {
    return (
      <div data-testid="autopilot-tile" className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
        <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Autopilot</div>
        <div className="mt-3 text-[#64748B] mono text-sm">loading…</div>
      </div>
    );
  }

  const s = data.state || {};
  const status = (s.status || "offline").toLowerCase();
  const meta = STATUS_META[status] || STATUS_META.offline;
  const StatusIcon = meta.Icon;

  const history = Array.isArray(data.history) ? data.history : [];
  const last10 = history.slice(-10).map((h) => ({
    cycle: `#${h.cycle}`,
    minutes: Number(h.minutes) || 0,
    result: h.result || "",
  }));

  const totalCycles = Number(s.total_cycles) || 0;
  const avgMin = history.length > 0
    ? (history.reduce((a, h) => a + (Number(h.minutes) || 0), 0) / history.length).toFixed(1)
    : "—";
  const lastResult = history.length > 0 ? history[history.length - 1].result : "—";
  // tick is used so elapsed re-renders every second
  const elapsed = status === "running" ? fmtElapsed(s.cycle_started_at) : "—";
  void tick;

  return (
    <div
      data-testid="autopilot-tile"
      className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4 relative overflow-hidden"
    >
      {/* Pulse accent when running */}
      {meta.pulse && (
        <div
          className="absolute top-0 left-0 right-0 h-[2px] animate-pulse"
          style={{ background: meta.color }}
        />
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Autopilot</div>
          <div className="mt-1 flex items-center gap-2">
            <StatusIcon size={18} weight="fill" style={{ color: meta.color }} />
            <span data-testid="autopilot-status" className="mono text-sm uppercase tracking-wider" style={{ color: meta.color }}>
              {meta.label}
            </span>
            {s.host && <span className="text-[10px] mono text-[#64748B]">· {s.host}</span>}
          </div>
          {s.stale_reason && (
            <div data-testid="autopilot-stale-reason" className="text-[10px] mono text-[#FFB400] mt-1 max-w-md">
              ⚠ {s.stale_reason}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <PulseIcon size={12} className="text-[#64748B]" />
          <span className="text-[10px] mono text-[#64748B]">
            updated {s.synced_at ? new Date(s.synced_at).toLocaleTimeString() : "—"}
          </span>
        </div>
      </div>

      {/* Big stat row */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Cycle</div>
          <div data-testid="autopilot-cycle" className="mono text-xl md:text-2xl mt-1" style={{ color: meta.color }}>
            #{Number(s.current_cycle) || 0}
          </div>
        </div>
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Elapsed</div>
          <div data-testid="autopilot-elapsed" className="mono text-xl md:text-2xl mt-1 text-white">{elapsed}</div>
        </div>
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Avg / cycle</div>
          <div className="mono text-xl md:text-2xl mt-1 text-white">{avgMin}{avgMin !== "—" ? "m" : ""}</div>
        </div>
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Completed</div>
          <div className="mono text-xl md:text-2xl mt-1 text-white">{totalCycles}</div>
        </div>
      </div>

      {/* Bar chart */}
      <div className="mt-4">
        <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-2">
          Last {last10.length || 0} cycles · minutes per cycle
        </div>
        {last10.length === 0 ? (
          <div className="text-[#64748B] mono text-xs py-6 text-center border border-dashed border-[#2A2D3A] rounded">
            No cycles recorded yet. Once <span className="text-[#D6FF38]">Run_Forever.ps1</span> kicks off, this tile will light up.
          </div>
        ) : (
          <div className="h-28">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={last10} margin={CHART_MARGIN}>
                <XAxis
                  dataKey="cycle"
                  tickLine={false}
                  axisLine={false}
                  tick={X_TICK_STYLE}
                />
                <Tooltip
                  cursor={CURSOR_STYLE}
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v, _n, p) => [`${Number(v).toFixed(1)} min · ${p.payload.result}`, "duration"]}
                />
                <Bar dataKey="minutes" radius={[2, 2, 0, 0]} minPointSize={6}>
                  {last10.map((d) => (
                    <Cell key={d.cycle} fill={BAR_COLOR(d.result)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {lastResult && lastResult !== "OK" && lastResult !== "—" && (
        <div className="mt-3 text-[11px] mono text-[#FFB400]">
          Last cycle finished with result: <span className="text-[#FF4D4D]">{lastResult}</span>
        </div>
      )}
    </div>
  );
}
